import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import {
  directionFromKey,
  findClosestTabbableDescendantInCell,
  findContainingGridCell,
  findJumpTarget,
  findNextFocusableAfterContainer,
  findPreviousFocusableBeforeContainer,
  findStructuralNeighbor,
  focusGridNavCell,
  isGridEscapeEdge,
  isPointerEventOnInnerTabbable,
  jumpActionFromKeyboardEvent,
  shouldDelegateTypingToField
} from './gridKeyboardNav';

export interface UseGridKeyboardNavigationOptions {
  enabled?: boolean;
  /**
   * Invoked on Escape (bubble phase, after overlays) when focus is in a gridcell and not typing in
   * an input. Typical use: move focus to a floating toolbar.
   */
  onEscapeToToolbar?: () => void;
}

/**
 * Listens for keys when focus is inside `containerRef` (`role="grid"`):
 * - Arrows: {@link findStructuralNeighbor}; at the escape edge (last row + Down, last cell of last
 *   row + Right, first row + Up, first cell of first row + Left), focus moves to the next/previous
 *   sequential tab stop outside the grid.
 * - Home / End: first/last cell in the current row
 * - Ctrl/Cmd+Home / Ctrl/Cmd+End: first cell of the first row / last cell of the last row
 * - Optional {@link UseGridKeyboardNavigationOptions.onEscapeToToolbar}: Escape from a gridcell
 *   (bubble phase so open popovers can handle Escape first; skipped in text fields).
 *
 * Tab follows the browser default order. Does not steal keys from inputs/textareas/contenteditable
 * (see {@link shouldDelegateTypingToField}).
 * Primary-button mousedown on a gridcell whose hit target is not already on an inner tab stop:
 * prevents the roving cell from taking focus and focuses the tabbable descendant closest to the
 * pointer in viewport space (so e.g. Enter activates a nearby dropdown trigger).
 * Primary-button double-click on the same kind of hit: focuses that closest tabbable and calls
 * {@link HTMLElement.click} on it so a second gesture opens toggles/menus that expect a click.
 */
export function useGridKeyboardNavigation(
  containerRef: RefObject<HTMLElement | null>,
  options?: UseGridKeyboardNavigationOptions
): void {
  const enabled = options?.enabled !== false;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return;
      }
      const root = containerRef.current;
      if (!root || root.getAttribute('role') !== 'grid') {
        return;
      }

      const active = document.activeElement;
      if (!(active instanceof HTMLElement) || !root.contains(active)) {
        return;
      }

      if (shouldDelegateTypingToField(active)) {
        return;
      }

      const currentCell = findContainingGridCell(root, active);
      if (!currentCell) {
        return;
      }

      const jump = jumpActionFromKeyboardEvent(event);
      if (jump) {
        const target = findJumpTarget(root, currentCell, jump);
        if (target) {
          event.preventDefault();
          focusGridNavCell(target);
        }
        return;
      }

      const direction = directionFromKey(event.key);
      if (!direction) {
        return;
      }

      const nextEl = findStructuralNeighbor(root, currentCell, direction);
      if (nextEl) {
        event.preventDefault();
        focusGridNavCell(nextEl);
        return;
      }

      if (!isGridEscapeEdge(root, currentCell, direction)) {
        return;
      }

      const outside =
        direction === 'down' || direction === 'right'
          ? findNextFocusableAfterContainer(root)
          : findPreviousFocusableBeforeContainer(root);
      if (outside) {
        event.preventDefault();
        outside.focus();
      }
    };

    const onMouseDownCapture = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) {
        return;
      }
      const root = containerRef.current;
      if (!root || root.getAttribute('role') !== 'grid') {
        return;
      }
      const t = event.target;
      if (!(t instanceof HTMLElement) || !root.contains(t)) {
        return;
      }
      const cell = findContainingGridCell(root, t);
      if (!cell) {
        return;
      }
      if (isPointerEventOnInnerTabbable(cell, t)) {
        return;
      }
      const inner = findClosestTabbableDescendantInCell(
        cell,
        event.clientX,
        event.clientY
      );
      if (!inner) {
        return;
      }
      event.preventDefault();
      inner.focus();
    };

    const onDoubleClickCapture = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) {
        return;
      }
      const root = containerRef.current;
      if (!root || root.getAttribute('role') !== 'grid') {
        return;
      }
      const t = event.target;
      if (!(t instanceof HTMLElement) || !root.contains(t)) {
        return;
      }
      const cell = findContainingGridCell(root, t);
      if (!cell) {
        return;
      }
      if (isPointerEventOnInnerTabbable(cell, t)) {
        return;
      }
      const inner = findClosestTabbableDescendantInCell(
        cell,
        event.clientX,
        event.clientY
      );
      if (!inner) {
        return;
      }
      event.preventDefault();
      inner.focus();
      inner.click();
    };

    const onEscapeBubble = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) {
        return;
      }
      const root = containerRef.current;
      if (!root || root.getAttribute('role') !== 'grid') {
        return;
      }
      const active = document.activeElement;
      if (!(active instanceof HTMLElement) || !root.contains(active)) {
        return;
      }
      if (shouldDelegateTypingToField(active)) {
        return;
      }
      const currentCell = findContainingGridCell(root, active);
      if (!currentCell) {
        return;
      }
      const toToolbar = optionsRef.current?.onEscapeToToolbar;
      if (!toToolbar) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      toToolbar();
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onMouseDownCapture, true);
    document.addEventListener('dblclick', onDoubleClickCapture, true);
    document.addEventListener('keydown', onEscapeBubble, false);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onMouseDownCapture, true);
      document.removeEventListener('dblclick', onDoubleClickCapture, true);
      document.removeEventListener('keydown', onEscapeBubble, false);
    };
  }, [containerRef, enabled]);
}
